import React, { useCallback, useState } from 'react';
import { get } from 'lodash';
import { useFieldArray } from 'react-hook-form';
import { ATMForm, ATMField, ATMRadio, ATMGrid } from 'shared-it-appmod-ui';
import { useRequestContext } from 'src/contexts/request.context';
import Lang from 'src/libraries/language';
import { IRequestFormMethods } from 'src/models/request.model';
import Confirm from 'src/components/atoms/confirm/confirm.component';
import FileManager from 'src/components/molecules/file-manager/file-manager.component';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';
import { ToastSuccess } from 'src/components/atoms/toaster/toaster.component';
import { FileCategory } from 'src/constants';
import { IFile } from 'src/models/file.model';
import styles from '../../molecules/form-flag/form-flag.module.scss';

type IProps = IRequestFormMethods & {
  label: string;
  name: string;
  listName: string;
  allowedTypes: FileCategory[];
  note?: string;
  no?: boolean;
  na?: boolean;
  setValue?: (value: any) => void
};

const FormFlagUpload: React.FC<IProps> = ({
  control,
  errors,
  label,
  name,
  listName,
  allowedTypes,
  note = '',
  no = true,
  na = false,
  setValue
}) => {
  const { actions } = useRequestContext();
  const [isConfirm, setIsConfirm] = useState(false);
  const [flag, setFlag] = useState<boolean | undefined | null>(
    get(control, `defaultValuesRef.current.${name}`)
  );
  const [deleteLoader, setDeleteLoader] = useState(false);

  const { fields, append, remove } = useFieldArray<IFile>({
    control,
    name: listName,
  });

  const handleUpdate = useCallback(
    (data) => {
      remove();
      append(data);
    },
    [append, remove]
  );

  const handleFlag = useCallback(
    (value) => {
      let isChecked;
      if (value === 'na') {
        isChecked = null;
      } else {
        isChecked = value === 'yes';
      }
      if (!isChecked && fields.find((field) => field.docName)) {
        setIsConfirm(true);
        setFlag(isChecked);
      }
      if (setValue) {
        setValue(isChecked);
      }
      control.setValue(name, isChecked, {
        shouldValidate: true,
      });

      setFlag(isChecked);
    },
    [control, name, fields, setFlag, setIsConfirm]
  );

  const handleRemove = useCallback(
    async (index, filename) => {
      setDeleteLoader(true);
      await actions.uploadDELETE(filename);
      remove(index);
      setDeleteLoader(false);
    },
    [actions, remove, setDeleteLoader]
  );

  const handleDelete = useCallback(async () => {
    setDeleteLoader(true);
    await Promise.all(
      fields.map((value, index) => handleRemove(index, value.fileName))
    );

    fields.forEach((value) => {
      ToastSuccess(
        Lang.formatString(Lang.MSG_FILE_DELETE_SUCCESS, value?.docName ?? '')
      );
    });

    setIsConfirm(false);
    setDeleteLoader(false);
  }, [control, name, fields, setFlag, setIsConfirm, setDeleteLoader]);

  return (
    <>
      <ATMForm.Field name={name} error={!!get(errors, name, false)} inline>
        <ATMGrid style={{ padding: '0' }}>
          <ATMGrid.Row columns="equal" style={{ padding: '0' }}>
            <ATMGrid.Column style={{ padding: '0' }}>
              {label !== '' && <LabelRequired>{label}</LabelRequired>}
              {note && <div className={styles.note}>({note})</div>}
            </ATMGrid.Column>
            <ATMGrid.Column style={{ padding: '0' }}>
              <ATMField
                key={`flag_${name}`}
                className="hidden"
                as={ATMForm.Input}
                control={control}
                type="hidden"
                name={name}
              />
              <ATMForm.Group>
                <ATMForm.Field>
                  <ATMRadio
                    label={Lang.LBL_YES}
                    value="yes"
                    checked={flag === true}
                    onChange={(_, { value }) => handleFlag(value)}
                  />
                </ATMForm.Field>
                {no && (
                  <ATMForm.Field>
                    <ATMRadio
                      label={Lang.LBL_NO}
                      value="no"
                      checked={flag === false}
                      onChange={(_, { value }) => handleFlag(value)}
                    />
                  </ATMForm.Field>
                )}
                {na && (
                  <ATMForm.Field>
                    <ATMRadio
                      label={Lang.LBL_NA}
                      value="na"
                      checked={flag === null || flag === false}
                      onChange={(_, { value }) => handleFlag(value)}
                    />
                  </ATMForm.Field>
                )}
              </ATMForm.Group>
            </ATMGrid.Column>
          </ATMGrid.Row>
        </ATMGrid>
      </ATMForm.Field>

      {flag && (
        <>
          {fields.map((item, i) => (
            <>
              {item.uploadId && (
                <ATMField
                  key={`id_${listName}_${i}_${item.fileName}`}
                  name={`${listName}[${i}].uploadId`}
                  defaultValue={item.uploadId}
                  className="hidden"
                  as={ATMForm.Input}
                  control={control}
                  type="hidden"
                />
              )}
              <ATMField
                key={`uploadTypeId_${listName}_${i}_${item.fileName}`}
                name={`${listName}[${i}].uploadTypeId`}
                defaultValue={item.uploadTypeId}
                className="hidden"
                as={ATMForm.Input}
                control={control}
                type="hidden"
              />
              <ATMField
                key={`filename_${listName}_${i}_${item.fileName}`}
                name={`${listName}[${i}].fileName`}
                defaultValue={item.fileName}
                className="hidden"
                as={ATMForm.Input}
                control={control}
                type="hidden"
              />
              <ATMField
                key={`docName_${listName}_${i}_${item.fileName}`}
                name={`${listName}[${i}].docName`}
                defaultValue={item.docName}
                className="hidden"
                as={ATMForm.Input}
                control={control}
                type="hidden"
              />
            </>
          ))}

          <FileManager
            name={listName}
            files={fields as IFile[]}
            allowedTypes={allowedTypes}
            inline
            onUpdate={handleUpdate}
            error={!!get(errors, listName, false)}
          />
        </>
      )}

      <Confirm
        open={isConfirm}
        size="tiny"
        onCancel={() => setIsConfirm(false)}
        header={Lang.TTL_CONFIRM_DELETE}
        content={Lang.formatString(Lang.MSG_CONFIRM_DELETE_FILES, label)}
        loading={deleteLoader}
        onConfirm={handleDelete}
        confirmText={Lang.LBL_CONFIRM_DELETE}
      />
    </>
  );
};

export default FormFlagUpload;
